'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface ImpactMetric {
  icon: string;
  value: string;
  label: string;
  description: string;
  color: string;
}

interface ImpactMetricCardProps {
  metric: ImpactMetric;
  index: number;
}

const ImpactMetricCard = ({ metric, index }: ImpactMetricCardProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (!isHydrated) return;

    const timer = setTimeout(() => {
      setIsVisible(true);
    }, index * 150);

    return () => clearTimeout(timer);
  }, [isHydrated, index]);

  return (
    <div
      className={`bg-card p-8 rounded-xl shadow-warm hover:shadow-warm-lg transition-all duration-500 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
    >
      <div className={`w-16 h-16 ${metric.color} rounded-full flex items-center justify-center mb-6`}>
        <Icon name={metric.icon as any} size={32} className="text-white" />
      </div>
      <div className="text-4xl md:text-5xl font-heading font-bold text-text-primary mb-2">
        {metric.value}
      </div>
      <h3 className="text-xl font-heading font-bold text-text-primary mb-3">{metric.label}</h3>
      <p className="text-text-secondary leading-relaxed">{metric.description}</p>
    </div>
  );
};

interface ImpactSectionProps {
  className?: string;
}

const ImpactSection = ({ className = '' }: ImpactSectionProps) => {
  const impactMetrics: ImpactMetric[] = [
    {
      icon: 'UserGroupIcon',
      value: '2M+',
      label: 'Active Listeners',
      description: 'GenZ and millennial Indians discovering authentic stories in their native languages every day.',
      color: 'bg-primary'
    },
    {
      icon: 'MicrophoneIcon',
      value: '5,000+',
      label: 'Content Creators',
      description: 'Regional storytellers, podcasters, and audio creators sharing their voices on our platform.',
      color: 'bg-secondary'
    },
    {
      icon: 'BookOpenIcon',
      value: '50K+',
      label: 'Stories Published',
      description: 'From folk tales to modern podcasts, covering romance, thriller, comedy, and cultural narratives.',
      color: 'bg-accent'
    },
    {
      icon: 'GlobeAltIcon',
      value: '15+',
      label: 'Indian Languages',
      description: 'Supporting linguistic diversity from Hindi and Tamil to Bengali, Marathi, and regional dialects.',
      color: 'bg-success'
    },
    {
      icon: 'MapPinIcon',
      value: '28',
      label: 'States Covered',
      description: 'Connecting communities across India with localized content and regional storytelling hubs.',
      color: 'bg-warning'
    },
    {
      icon: 'ChartBarIcon',
      value: '100M+',
      label: 'Stories Streamed',
      description: 'Hours of audio content consumed, preserving oral traditions in digital format.',
      color: 'bg-error'
    }
  ];

  const culturalImpacts = [
    {
      icon: 'SparklesIcon',
      title: 'Preserving Oral Traditions',
      description: 'Digitizing and celebrating India\'s rich heritage of storytelling across generations.'
    },
    {
      icon: 'UserGroupIcon',
      title: 'Empowering Regional Voices',
      description: 'Providing platform for underrepresented storytellers to reach national audiences.'
    },
    {
      icon: 'HeartIcon',
      title: 'Building Cultural Bridges',
      description: 'Connecting diverse Indian communities through shared stories and experiences.'
    },
    {
      icon: 'AcademicCapIcon',
      title: 'Educational Impact',
      description: 'Making learning accessible through audio content in native languages.'
    }
  ];

  return (
    <section className={`py-20 md:py-32 bg-muted ${className}`}>
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-success/10 rounded-full mb-6">
            <Icon name="ChartBarIcon" size={20} className="text-success" />
            <span className="text-sm font-semibold text-success">Our Impact</span>
          </div>

          <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-text-primary mb-6">
            Transforming India's Audio Landscape
          </h2>

          <p className="text-lg md:text-xl text-text-secondary max-w-3xl mx-auto leading-relaxed">
            Since our launch, we've been on a mission to celebrate Indian audio content and support the storytelling community across the nation.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
          {impactMetrics.map((metric, index) => (
            <ImpactMetricCard key={index} metric={metric} index={index} />
          ))}
        </div>

        <div className="bg-gradient-to-br from-primary to-primary-dark p-8 md:p-12 rounded-2xl text-white mb-16">
          <h3 className="text-2xl md:text-3xl font-heading font-bold text-center mb-12">
            Cultural Impact & Community Growth
          </h3>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {culturalImpacts.map((impact, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name={impact.icon as any} size={28} className="text-white" />
                </div>
                <h4 className="text-lg font-heading font-bold mb-2">{impact.title}</h4>
                <p className="text-white/80 text-sm leading-relaxed">{impact.description}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-card p-8 md:p-12 rounded-2xl shadow-warm-lg text-center">
          <Icon name="TrophyIcon" size={48} className="text-accent mx-auto mb-6" />
          <h3 className="text-2xl md:text-3xl font-heading font-bold text-text-primary mb-4">
            Recognition & Awards
          </h3>
          <div className="flex flex-wrap justify-center gap-6 text-text-secondary">
            <div className="flex items-center space-x-2">
              <Icon name="StarIcon" size={20} className="text-accent" variant="solid" />
              <span>Best Audio Platform 2025 - Digital India Awards</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="StarIcon" size={20} className="text-accent" variant="solid" />
              <span>Cultural Innovation Award - NASSCOM</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="StarIcon" size={20} className="text-accent" variant="solid" />
              <span>Top 10 Startups - YourStory Tech30</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ImpactSection;